import { useState, useEffect } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Card,
  Container,
  IconButton,
  List,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { getInputLabelProps, getInputProps } from "../../../common/appStyles";
import localize from "../../../common/localize";
import useGlobalContext from "../../../hooks/useGlobalContext";
import Combobox from "../../../components/common/Combobox";
import { Add, Delete, Search } from "@mui/icons-material";
import CreatedByText from "../../../components/common/CreatedByText";
import AddressManagement from "../../../components/common/AddressManagement";
import ExpandableView from "../../../components/common/ExpandableView";
import ModalBackground from "../../../components/common/ModalBackground";
import NotificationItem from "../../../components/common/NotificationItem";
import DeleteConfirmationMessage from "../../../components/common/DeleteConfirmationMessage";

export default function AddOrEditPersonal() {
  const {
    WAREHOUSE_ENDPOINT,
    POSITION_ENDPOINT,
    CONTRACTOR_API,
    PERSONAL_API,
    personalModel,
    setPersonalModel,
    config,
    toggleVisibleModalConfirm,
  } = useGlobalContext();

  const [positionData, setPositionData] = useState(null);
  const [contractorsData, setContractorsData] = useState(null);
  const [warehouses, setWarehouses] = useState(null);
  const [showWarning, setShowWarning] = useState({ show: false });
  const [showZoneSelector, setShowZoneSelector] = useState(false);
  const [selectedZone, setSelectedZone] = useState(null);
  const [executionResult, setExecutionResult] = useState(null);
  const [selectedItemToDelete, setSelectedItemToDelete] = useState(null);

  const getWarehouses = async () => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${WAREHOUSE_ENDPOINT}/`, requestConfig);

    setWarehouses(data?.data);
  };

  const getPositionData = async () => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${POSITION_ENDPOINT}/`, requestConfig);

    setPositionData(data?.data);
  };

  const getContractorsData = async () => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${CONTRACTOR_API}/`, requestConfig);

    setContractorsData(data?.data);
  };

  useEffect(() => {
    getPositionData();
    getContractorsData();
    getWarehouses();
  }, []);

  const handleChange = (e) => {
    setPersonalModel({
      ...personalModel,
      [e.target.name]: e.target.value,
    });
  };

  const handleIdentificationTypeChange = (identificationType) => {
    if (identificationType) {
      setPersonalModel({
        ...personalModel,
        IdentificationType: identificationType,
      });
    }
  };

  const handleContractorChange = (currentContractor) => {
    if (!currentContractor) {
      setPersonalModel({
        ...personalModel,
        Contractor: {},
        ContractorId: null,
      });
      return;
    }

    setPersonalModel({
      ...personalModel,
      Contractor: currentContractor,
      ContractorId: currentContractor.Id,
    });
  };

  const handlePositionChange = (currentPosition) => {
    if (!currentPosition) {
      setPersonalModel({
        ...personalModel,
        Position: {},
        PositionId: null,
      });
      return;
    }

    setPersonalModel({
      ...personalModel,
      Position: currentPosition,
      PositionId: currentPosition.Id,
    });
  };

  const handleWarehouseChange = (e) => {
    if (!e) {
      setPersonalModel({
        ...personalModel,
        Warehouse: null,
        WarehouseId: 0,
      });
      return;
    }

    setPersonalModel({
      ...personalModel,
      Warehouse: e,
      WarehouseId: e.Id,
    });
  };

  const isSearchAvailable = () =>
    personalModel?.Identification?.replaceAll(" ", "").length > 0;

  const getPersonalInfoByIdentification = async () => {
    try {
      if (personalModel?.Identification?.replaceAll(" ", "").length === 0) {
        return;
      }
      setShowWarning({ show: false });
      setIsLoading(true);
      const filteringConfig = {
        ...config,
        headers: {
          ...config.headers,
          Filter: JSON.stringify([
            {
              Name: "Identification",
              FriendlyName: localize.identification,
              FilterValue: { Value: personalModel.Identification },
              Value: personalModel.Identification,
            },
            {
              Name: "IdentificationType",
              FriendlyName: localize.identification_type,
              FilterValue: { Value: personalModel.IdentificationType.Id },
              Value: personalModel.IdentificationType.Id,
            },
          ]),
          pagination: -1,
        },
      };
      const data = await axios.get(PERSONAL_API, filteringConfig);

      if (data?.data?.Data && data?.data?.Data.length > 0) {
        setPersonalModel({
          ...data?.data?.Data[0],
          IdentificationType: {
            Id: data?.data?.Data[0].IdentificationType,
            Name: data?.data?.Data[0].IdentificationType,
          },
        });

        setShowWarning({
          show: true,
          severity: "warning",
          message: localize.found_personal_warning,
        });
        return;
      }

      setShowWarning({
        show: true,
        severity: "info",
        message: localize.no_data,
      });
    } catch (error) {
      setShowWarning({
        show: true,
        severity: "error",
        message: `${localize.error_unexpected}: ${error}`,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onKeyUp = (e) => {
    if (e.key === "Enter") {
      getPersonalInfoByIdentification();
    }
  };

  const getGeneralInfoComponents = () => {
    return (
      <div>
        <Container
          sx={{
            paddingLeft: "0px !important;",
            paddingRight: "0px !important;",
          }}
          style={{ width: "100%" }}
        >
          <div className="w-full">
            <section className="flex justify-center content-center items-center ">
              <section id="ReportData" className="w-full">
                <div className="flex justify-between my-3">
                  <Combobox
                    isRequired
                    id="IdentificationType"
                    handleValueChange={handleIdentificationTypeChange}
                    fieldValue={personalModel?.IdentificationType ?? null}
                    fieldOptions={[
                      {
                        Id: "Cédula de ciudadanía",
                        Name: "Cédula de ciudadanía",
                      },
                      { Id: "Nit", Name: "Nit" },
                      { Id: "Pasaporte", Name: "Pasaporte" },
                      {
                        Id: "Cédula de extranjería",
                        Name: "Cédula de extranjería",
                      },
                      { Id: "Otro", Name: "Otro" },
                    ]}
                    fieldLabel={localize.identification_type}
                  />

                  <div className="mx-2" />

                  <Box className="flex gap-2 w-full items-center">
                    <TextField
                      required
                      InputLabelProps={getInputLabelProps()}
                      inputProps={getInputProps()}
                      size="small"
                      fullWidth
                      name="Identification"
                      value={personalModel.Identification}
                      id="Identification"
                      label={localize.identification}
                      variant="outlined"
                      onChange={handleChange}
                      onKeyUp={onKeyUp}
                      autoComplete="off"
                    />

                    <Tooltip title={localize.personal_search_tooltip}>
                      <button
                        disabled={!isSearchAvailable()}
                        style={{
                          color: isSearchAvailable() ? "#10366e" : "lightgray",
                        }}
                        onClick={getPersonalInfoByIdentification}
                      >
                        <Search
                          sx={{
                            width: "24px",
                            height: "24px",
                          }}
                        />
                      </button>
                    </Tooltip>
                  </Box>
                </div>

                {showWarning?.show && (
                  <Alert className="mb-3" severity={showWarning.severity}>
                    <label className="text-xs">{showWarning.message}</label>
                  </Alert>
                )}

                <div className="flex justify-between my-3">
                  <TextField
                    required
                    InputLabelProps={getInputLabelProps()}
                    inputProps={getInputProps()}
                    size="small"
                    fullWidth
                    name="SurName"
                    id="SurName"
                    value={personalModel.SurName}
                    label={localize.surName}
                    variant="outlined"
                    onChange={handleChange}
                    autoComplete="off"
                  />
                  <div className="mx-2" />
                  <TextField
                    required
                    InputLabelProps={getInputLabelProps()}
                    inputProps={getInputProps()}
                    size="small"
                    fullWidth
                    name="LastName"
                    id="LastName"
                    value={personalModel.LastName}
                    label={localize.lastName}
                    variant="outlined"
                    onChange={handleChange}
                    autoComplete="off"
                  />
                </div>

                <div className="flex justify-between my-3">
                  <Combobox
                    id="Contractors"
                    handleValueChange={handleContractorChange}
                    fieldValue={personalModel?.Contractor}
                    fieldOptions={contractorsData?.Data ?? []}
                    fieldLabel={localize.contractor}
                  />

                  <div className="mx-2" />

                  <Combobox
                    id="Position"
                    handleValueChange={handlePositionChange}
                    fieldValue={personalModel?.Position}
                    fieldOptions={positionData?.Data ?? []}
                    fieldLabel={localize.position}
                  />
                </div>

                <AddressManagement
                  model={personalModel}
                  setModel={setPersonalModel}
                  disableAddressComplement
                  disableZones
                />

                <div className="flex justify-between my-3">
                  <TextField
                    InputLabelProps={getInputLabelProps()}
                    inputProps={getInputProps()}
                    size="small"
                    fullWidth
                    name="PhoneNumber"
                    id="PhoneNumber"
                    value={personalModel.PhoneNumber}
                    label={localize.phone_number}
                    variant="outlined"
                    onChange={handleChange}
                    autoComplete="off"
                  />

                  <div className="mx-2" />

                  <TextField
                    InputLabelProps={getInputLabelProps()}
                    inputProps={getInputProps()}
                    size="small"
                    fullWidth
                    name="EmailAddress"
                    id="Email"
                    value={personalModel.EmailAddress}
                    label={localize.email}
                    variant="outlined"
                    onChange={handleChange}
                    autoComplete="off"
                  />

                  {/*<Combobox
                  id="Warehouse"
                  handleValueChange={handleWarehouseChange}
                  fieldValue={personalModel?.Warehouse}
                  fieldOptions={warehouses?.Data ?? []}
                  fieldLabel={localize.warehouse}
                /> */}
                </div>
              </section>
            </section>

            <CreatedByText entity={personalModel} />
          </div>
        </Container>
      </div>
    );
  };

  const getZonesComponents = () => {
    return (
      <Box className="flex flex-col gap-2 mt-2 w-full">
        <Box className="flex justify-between items-center w-full gap-2">
          <Alert severity="info">
            <span className="text-xs">
              {localize.zone_description_for_personal}
            </span>
          </Alert>
          <Button
            size="small"
            variant="contained"
            color="success"
            className="app_general_button_data app_success_button btn-sm"
            onClick={() => {
              setExecutionResult(null);
              setSelectedZone({
                Zone: null,
                ZoneId: 0,
              });
              setShowZoneSelector(true);
            }}
            sx={{ width: "200px" }}
          >
            <span className="mr-2">{localize.add}</span>
            <Add className="text-lg" />
          </Button>
        </Box>

        {executionResult && (
          <Alert className="w-full" severity={executionResult?.Severity}>
            <span className="text-xs">{executionResult?.Message}</span>
          </Alert>
        )}

        {showZonesList()}
      </Box>
    );
  };

  const getZonesFromPersonal = async () => {
    try {
      const result = await axios.get(
        `${PERSONAL_API}/${personalModel?.id}`,
        config,
      );

      

      if (result?.data?.Ok && result.data?.Data) {
        setPersonalModel({
          ...personalModel,
          Zones:
            result.data.Data.Zones?.length > 0
              ? [...result.data.Data.Zones]
              : [],
        });
      }
    } catch (ex) {
      setExecutionResult({
        Severity: "error",
        Message: `${localize.error_unexpected}: ${ex.message}`,
      });
    }
  };

  const handleZoneAssignment = async (zoneData) => {
    if (!zoneData) {
      setExecutionResult({
        Severity: "error",
        Message: localize.no_zone_data,
      });
      setShowZoneSelector(false);
      return;
    }

    try {
      const result = await axios.post(
        `${PERSONAL_API}/zone`,
        {
          personalId: personalModel?.id,
          locationId: zoneData?.ZoneId,
        },
        config,
      );

      if (result?.data?.Ok) {
        setExecutionResult({
          Severity: "success",
          Message: localize.zone_added_successfully,
        });
        getZonesFromPersonal();
        return;
      }

      setExecutionResult({
        Severity: "error",
        Message: `${localize.zone_add_failed} ${
          result?.data ? JSON.stringify(result?.data) : ""
        }`,
      });
    } catch (ex) {
      setExecutionResult({
        Severity: "error",
        Message: `${localize.error_unexpected}: ${JSON.stringify(ex)}`,
      });
    } finally {
      setShowZoneSelector(false);
    }
  };

  const handleZoneRemoval = async (id) => {
    if (!id || id === 0) {
      setExecutionResult({
        Severity: "error",
        Message: localize.no_zone_data,
      });
      toggleVisibleModalConfirm();
      return;
    }

    try {
      const result = await axios.delete(
        `${PERSONAL_API}/${personalModel?.id}/zone/${id}`,
        config,
      );

      if (result?.data?.Ok) {
        setExecutionResult({
          Severity: "success",
          Message: localize.zone_deleted_successfully,
        });
        getZonesFromPersonal();
        return;
      }

      setExecutionResult({
        Severity: "error",
        Message: `${localize.zone_delete_failed} ${
          result?.data ? result?.data : ""
        }`,
      });
    } catch (ex) {
      setExecutionResult({
        Severity: "error",
        Message: `${localize.error_unexpected}: ${ex.message}`,
      });
    } finally {
      toggleVisibleModalConfirm();
    }
  };

  const showZonesList = () => {
    return (
      <Box className="flex flex-col gap-2 mt-2">
        {personalModel?.Zones?.lenght > 0 && (
          <Typography>
            <span className="text-xs">{`${localize.zones}: ${personalModel?.Zones?.lenght}`}</span>
          </Typography>
        )}
        <List className="flex flex-col gap-2 mt-2">
          {!personalModel?.Zones || personalModel?.Zones?.length === 0 ? (
            <NotificationItem notification={{ data: localize.no_data }} />
          ) : (
            personalModel?.Zones.map((item) => (
              <Card
                variant="outlined"
                className="w-full flex justify-between gap-2 p-2"
                key={item.Id}
              >
                <Box className="flex gap-2 items-center">
                  <IconButton
                    onClick={() => {
                      setExecutionResult(null);
                      setSelectedItemToDelete(item);
                      toggleVisibleModalConfirm();
                    }}
                  >
                    <Delete
                      sx={{ color: "red", width: "16px", height: "16px" }}
                    />
                  </IconButton>

                  <Typography>
                    <span className="text-xs">{`${item.Code} - ${item.Name}`}</span>
                  </Typography>
                </Box>
              </Card>
            ))
          )}
        </List>
      </Box>
    );
  };

  return (
    <Box className="flex flex-col gap-2 mt-2 w-full">
      <DeleteConfirmationMessage
        message={localize.unassign_zone_message}
        title={localize.unassign}
        entityName={selectedItemToDelete?.Name}
        entityType={localize.zone}
        onClickForCancel={() => {
          toggleVisibleModalConfirm();
          setSelectedItemToDelete(null);
        }}
        onClickForConfirm={() => {
          handleZoneRemoval(selectedItemToDelete?.Id);
        }}
      />

      {showZoneSelector && (
        <ModalBackground
          title={localize.zone_selector_title}
          showModal={showZoneSelector}
          setShowModal={setShowZoneSelector}
          Component={
            <Box sx={{ width: "500px" }}>
              <AddressManagement
                model={selectedZone}
                setModel={setSelectedZone}
                disableAddress
                disableAddressComplement
              />
            </Box>
          }
          onClickToCancel={() => {
            setSelectedZone(null);
            setShowZoneSelector(false);
          }}
          onClickToSubmit={() => {
            handleZoneAssignment(selectedZone);
            setShowZoneSelector(false);
          }}
        />
      )}

      <ExpandableView
        components={
          personalModel?.id > 0
            ? [
                {
                  Name: localize.general_information,
                  Component: getGeneralInfoComponents(),
                },
                {
                  Name: localize.zones,
                  Component: getZonesComponents(),
                },
              ]
            : [
                {
                  Name: localize.general_information,
                  Component: getGeneralInfoComponents(),
                },
              ]
        }
        defaultExpandedIndex={0}
      />
    </Box>
  );
}
